import { Socket, Server } from 'socket.io';
import i18next from 'i18next';
import Container from 'typedi';
import { ChatService } from '../../Modules/Chat/services/chat.service';
import User from '../../Modules/Auth/models/auth.model';
import { HttpException } from '../../utils/exceptions/httpException';
import { status } from '../../utils/helpers/api.responses';
import mongoose from 'mongoose';
import Chat from '../../Modules/Chat/models/chat.model';

const connectedUsers = new Map<string, { socketId: string; lastHeartbeat: number }>();
const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const HEARTBEAT_TIMEOUT = 45000; // 45 seconds

const chatService = Container.get(ChatService);

const checkHeartbeats = setInterval(() => {
    const now = Date.now();
    for (const [userId, { socketId, lastHeartbeat }] of connectedUsers) {
        if (now - lastHeartbeat > HEARTBEAT_TIMEOUT) {
            connectedUsers.delete(userId);
            io.to(socketId)?.emit('user-status', { userId, onlineStatus: 'offline' });
            io.sockets.sockets.get(socketId)?.disconnect();
        }
    }
}, HEARTBEAT_INTERVAL);

let io: Server;

export const socketController = (socket: Socket, server: Server) => {
    io = server;
    console.log('Socket Controller connected:', socket.id);

    socket.on('join-room', async (payload: any) => {
        try {
            console.log("socket", socket)

            // if (typeof payload === 'string') {
            //     payload = JSON.parse(payload);
            // }
            // const { role, userId } = payload;
            // const userId = socket.handshake.headers.userId
            const userId = socket.handshake.auth.userId;

            // if (!role || !userId || !mongoose.isValidObjectId(userId)) {
            //     socket.emit('error', { message: 'Missing or invalid role or userId', status: status.BadRequest });
            //     return;
            // }

            const user = await User.findById(userId)
                .populate<{ roleId: { name: string } }>('roleId', 'name')
                .lean();
            if (!user || !user.roleId?.name) {
                socket.emit('error', {
                    message: 'User not found or role not defined',
                    status: status.BadRequest,
                });
                return;
            }

            const role = user.roleId.name;
            const normalizedRole = role.toLowerCase().replace(/\s+/g, '');
            const room = `${normalizedRole}_${userId}`;
            socket.join(room);
            console.log(`User ${userId} with role ${role} joined room: ${room}`);


            connectedUsers.set(user._id, { socketId: socket.id, lastHeartbeat: Date.now() });
            await notifyUserStatus(user._id, 'online', socket, io);
        } catch (err) {
            console.error('Failed to join room:', err);
            socket.emit('error', { message: 'Failed to join room', status: status.InternalServerError });
        }
    });

    socket.on('heartbeat', () => {
        const userId = socket.handshake.auth.userId;
        if (userId && mongoose.isValidObjectId(userId) && connectedUsers.has(userId)) {
            connectedUsers.set(userId, { socketId: socket.id, lastHeartbeat: Date.now() });
        }
    });

    socket.on('send-message', async (data: { chatId: string; content: string; receiverId: string; attachments?: string[] }) => {
        try {
            // const senderId = socket.handshake.auth.userId;
            // const senderId = socket.handshake.headers.userid
            const senderId = socket.handshake.auth.userId;

            const { chatId, content, receiverId, attachments } = data;
            console.log("data", data)
            // const invalidFields: string[] = [];

            // if (!mongoose.isValidObjectId(chatId)) invalidFields.push('chatId');
            // if (!mongoose.isValidObjectId(senderId)) invalidFields.push('senderId');
            // if (!mongoose.isValidObjectId(receiverId)) invalidFields.push('receiverId');
            // if (attachments && !attachments.every(id => mongoose.isValidObjectId(id))) invalidFields.push('attachmentIds');

            // if (invalidFields.length > 0) {
            //     socket.emit('error', {
            //         message: `Invalid field(s): ${invalidFields.join(', ')}`,
            //         status: status.BadRequest,
            //         invalidFields
            //     });
            //     return;
            // }


            const t = i18next.t.bind(i18next);
            console.log('Sending message:', { chatId, senderId, receiverId, content, attachments });

            const user = await User.findById(senderId)
                .populate<{ roleId: { name: string } }>('roleId', 'name')
                .lean();

            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }
            // const normalizedAttachmentIds: mongoose.Types.ObjectId[] = (attachments || [])
            //     .map(att => {
            //         const id = typeof att === 'string' ? att : att?._id?.toString?.();
            //         return mongoose.isValidObjectId(id) ? new mongoose.Types.ObjectId(id) : null;
            //     })
            //     .filter((id): id is mongoose.Types.ObjectId => !!id);


            const message = await chatService.sendMessage(
                chatId,
                senderId.toString(),
                receiverId,
                content,
                t,
                // normalizedAttachmentIds
                attachments?.map(a => new mongoose.Types.ObjectId(a)) || [],
            );

            const chat = await Chat.findById(chatId).lean();
            if (!chat) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Chat.chat') }));
            }

            const participantRooms: string[] = [];
            for (const p of chat.participants) {
                const participantUser = await User.findById(p)
                    .populate<{ roleId: { name: string } }>('roleId', 'name')
                    .lean();
                if (!participantUser || !participantUser.roleId) continue;

                const role = participantUser.roleId.name.toLowerCase().replace(/\s+/g, '');
                participantRooms.push(`${role}_${p.toString()}`);
            }

            io.to(participantRooms).emit('new-message', message);
            socket.emit('message-sent', { status: 'success', message });
        } catch (error) {
            console.error('Failed to send message:', error);
            socket.emit('error', {
                message: error instanceof HttpException ? error.message : 'Failed to send message',
                status: error instanceof HttpException ? error.status : status.InternalServerError,
                ...(process.env.NODE_ENV === 'development' && {
                    stack: error instanceof Error ? error.stack : undefined
                }),
            });
        }
    });


    socket.on('mark-message-read', async (data: { messageId: string }) => {
        try {
            const userId = socket.handshake.auth.userId;
            const { messageId } = data;

            if (!mongoose.isValidObjectId(messageId) || !mongoose.isValidObjectId(userId)) {
                socket.emit('error', { message: 'Invalid messageId or userId', status: status.BadRequest });
                return;
            }

            const t = i18next.t.bind(i18next);
            console.log('Marking message as read:', { messageId, userId });

            const result = await chatService.markMessageAsRead(messageId, userId, t);

            const chatId = (result.chatId as any)?._id?.toString?.() || result.chatId?.toString?.();
            if (!chatId) {
                throw new HttpException(status.BadRequest, t('General.invalidData', { field: t('Chat.chat') }));
            }
            const chat = await Chat.findById(chatId).lean();
            if (!chat) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Chat.chat') }));
            }

            const participantRooms: string[] = [];
            for (const p of chat.participants) {
                const participantUser = await User.findById(p).populate<{ roleId: { name: string } }>('roleId', 'name').lean();
                if (!participantUser || !participantUser.roleId) continue;
                const role = participantUser.roleId.name.toLowerCase().replace(/\s+/g, '');
                participantRooms.push(`${role}_${p.toString()}`);
            }

            io.to(participantRooms).emit('message-read', result);
            socket.emit('message-read-ack', { status: 'success', result });
        } catch (error) {
            console.error('Failed to mark message as read:', error);
            socket.emit('error', {
                message: error instanceof HttpException ? error.message : 'Failed to mark message as read',
                status: error instanceof HttpException ? error.status : status.InternalServerError,
                ...(process.env.NODE_ENV === 'development' && { stack: error instanceof Error ? error.stack : undefined }),
            });
        }
    });

    socket.on('disconnect', async () => {
        try {
            const userId = socket.handshake.auth.userId;
            if (userId && mongoose.isValidObjectId(userId)) {
                connectedUsers.delete(userId);
                await notifyUserStatus(userId, 'offline', socket, io);
            }
            console.log('Socket disconnected:', socket.id);
        } catch (error) {
            console.error('Failed to handle disconnect:', error);
        }
    });

    async function notifyUserStatus(userId: string, onlineStatus: 'online' | 'offline', socket: Socket, io: Server) {
        try {
            const user = await User.findById(userId).populate<{ roleId: { name: string } }>('roleId', 'name').lean();
            if (!user || !user.roleId) return;

            const normalizedRole = user.roleId.name.toLowerCase().replace(/\s+/g, '');
            const chats = await Chat.find({ participants: userId, isDeleted: false }).lean();
            const participantIds = chats.flatMap(c => c.participants.map(p => p.toString())).filter(id => id !== userId);
            const participantRooms = participantIds.map(id => `${normalizedRole}_${id}`);
            io.to(participantRooms).emit('user-status', { userId, onlineStatus });
        } catch (error) {
            console.error('Failed to notify user status:', error);
        }
    }
};